<template>
  <div class="h-screen overflow-hidden flex flex-col justify-between bg-white border-solid border-l border-0 border-gray-border">
    <div>
      <div class="border-solid border-b border-0 border-gray-border px-8 pb-8">
        <h3 class="font-semibold mt-8 truncate">
          <span class="text-gray-dark">{{ botDisplayName }}</span>
          <span class="text-gray ml-1">/</span>
          <span class="text-black ml-1">Add market</span>
        </h3>
      </div>

      <div class="px-8">
        <div v-if="hasExistingMarketOptions" class="mt-8">
          <base-label id="market-mode" label="Market source"/>
          <BaseToggleGroup class="mt-2"
                           :tabs="toggleTabs"
                           v-model="mode"
          />
        </div>

        <div v-if="hasExistingMarketOptions && mode === MARKET_MODE.EXISTING" class="mt-6">
          <base-label id="existingMarketSelect" label="Select Market"/>
          <queryable-select id="market"
                            :value="selectedMultiBotId"
                            :options="availableMultiBotOptions"
                            class="w-full mt-1 h-10"
                            @input="selectedMultiBotId = $event"
          />
        </div>

        <div v-else class="mt-6">
          <base-label id="newMarketName" label="Name"/>
          <base-input id="newMarketName"
                      name="newMarketName"
                      variant="editor"
                      type="string"
                      class="w-full border-gray py-px mt-1 h-10"
                      v-model.trim="newMarketName"
          />

          <div class="mt-6">
            <base-label id="newMarketLanguage" label="Language"/>
            <queryable-select id="newMarketLanguage"
                              :value="newMarketLanguage"
                              :options="languageOptions"
                              class="w-full mt-1 h-10"
                              @input="newMarketLanguage = $event"
            />
          </div>

          <div class="mt-6">
            <base-label id="newMarketTimezone" label="Timezone"/>
            <queryable-select id="newMarketTimezone"
                              :value="newMarketTimezone"
                              :options="timezoneOptions"
                              class="w-full mt-1 h-10"
                              @input="newMarketTimezone = $event"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-4 mb-6 px-8">
      <busy-button id="addMarketCreateButton"
                   variant="primary"
                   class="col-span-1"
                   :is-loading="isLoading"
                   :is-disabled="isCreateDisabled || isLoading"
                   @click="submit"
      >
        Create
      </busy-button>
      <base-button variant="bordered"
                   class="col-span-1 justify-center"
                   @click="$emit('cancel')"
      >
        Cancel
      </base-button>
    </div>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import BaseToggleGroup from '@/components/base-components/BaseToggleGroup.vue'
import BaseButton from '@/components/base-components/BaseButton.vue'
import QueryableSelect from '@/components/common/QueryableSelect.vue'
import { BOT_LANGUAGES, BOT_TIMEZONES } from '@/utils/bot.constants'

const MARKET_MODE = {
  EXISTING: 'existing',
  NEW: 'new'
}

export default {
  name: 'SettingsBotAddMarketEditor',

  components: {
    BusyButton,
    BaseToggleGroup,
    BaseButton,
    QueryableSelect
  },

  props: {
    bot: {
      type: Object,
      required: true
    },
    multiBots: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },

  emits: ['cancel', 'create'],

  data() {
    return {
      MARKET_MODE,
      mode: MARKET_MODE.EXISTING,
      selectedMultiBotId: null,
      newMarketName: '',
      newMarketLanguage: this.bot.language || '',
      newMarketTimezone: this.bot.timezone || 'Europe/Prague'
    }
  },

  computed: {
    toggleTabs() {
      return [
        { key: MARKET_MODE.EXISTING, label: 'Use Existing Market' },
        { key: MARKET_MODE.NEW, label: 'Create New Market' }
      ]
    },
    availableMultiBotOptions() {
      return this.multiBots
        .filter(multiBot => multiBot.primaryBot.multi_bot_id !== this.bot.multi_bot_id)
        .map(multiBot => ({
          value: multiBot.id,
          text: multiBot.name
        }))
    },
    hasExistingMarketOptions() {
      return this.availableMultiBotOptions.length > 0
    },
    languageOptions() {
      return BOT_LANGUAGES
    },
    timezoneOptions() {
      return BOT_TIMEZONES.map(timezone => ({
        text: timezone,
        value: timezone
      }))
    },
    botDisplayName() {
      return this.bot.multi_bot_name || this.bot.name
    },
    isCreateDisabled() {
      if (this.mode === MARKET_MODE.EXISTING) return !this.selectedMultiBotId
      return !this.newMarketName || !this.newMarketLanguage || !this.newMarketTimezone
    }
  },

  methods: {
    submit() {
      this.$emit('create', {
        mode: this.mode,
        sourceMultiBotId: this.selectedMultiBotId,
        name: this.newMarketName,
        language: this.newMarketLanguage,
        timezone: this.newMarketTimezone
      })
    }
  },

  watch: {
    hasExistingMarketOptions: {
      immediate: true,
      handler(hasOptions) {
        if (!hasOptions) {
          this.mode = MARKET_MODE.NEW
          this.selectedMultiBotId = null
        }
      }
    }
  }
}
</script>
